/**
 * useCreditPaymentSocket - Socket listener for credit payment confirmations.
 * Pattern: identical to useUnreadCountSocket.
 */

import { useEffect, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS as Events } from '@troia-v3/types';
import type { CreditPaymentConfirmedEvent } from '@troia-v3/types';
import { useAuth } from '@/contexts/AuthContext';

export const useCreditPaymentSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const { state: authState } = useAuth();
  const [confirmedPayment, setConfirmedPayment] = useState<CreditPaymentConfirmedEvent | null>(null);

  const resetConfirmedPayment = useCallback(() => {
    setConfirmedPayment(null);
  }, []);

  useEffect(() => {
    if (!socket || !authState.user) return;

    const handlePaymentConfirmed = (data: CreditPaymentConfirmedEvent) => {
      setConfirmedPayment(data);

      toast.success(`Pagamento PIX confirmado! ${data.creditsPerCycle.toLocaleString('pt-BR')} créditos adicionados.`);

      queryClient.invalidateQueries({ queryKey: ['credits-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['credits', 'consumption-by-day'] });
      queryClient.invalidateQueries({ queryKey: ['credits', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    };

    socket.on(Events.CREDIT_PAYMENT_CONFIRMED, handlePaymentConfirmed);

    return () => {
      socket.off(Events.CREDIT_PAYMENT_CONFIRMED, handlePaymentConfirmed);
    };
  }, [socket, authState.user, queryClient]);

  return { confirmedPayment, resetConfirmedPayment };
};
